/**
 * Email handling for the notify list. Pure — no I/O, no clock, no globals.
 *
 * Validation here is deliberately permissive. This is a notify list, not an auth
 * system: a valid address we reject is a lead we lose, and a malformed one we accept
 * costs a row. The asymmetry points one way, so the rule is "one @, something either
 * side, a dot in the domain" and nothing more.
 */

/** RFC 5321's forward-path limit. Past this, no real mailbox is on the other end. */
export const MAX_EMAIL_LENGTH = 254;

/** Trim and lowercase. Normalizing is not repairing — interior junk is left alone. */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Expects an already-normalized address. Callers get the pairing right by going
 * through `decideSignup`, which does both in order.
 */
export function isPlausibleEmail(normalized: string): boolean {
  if (normalized.length === 0 || normalized.length > MAX_EMAIL_LENGTH) return false;
  if (/\s/.test(normalized)) return false;

  const parts = normalized.split('@');
  if (parts.length !== 2) return false;

  const [local, domain] = parts as [string, string];
  if (local.length === 0 || domain.length === 0) return false;

  // A domain needs a dot with a label on each side. This is the whole domain check:
  // anything stricter starts rejecting real addresses to catch fake ones.
  if (!domain.includes('.')) return false;
  if (domain.startsWith('.') || domain.endsWith('.')) return false;
  if (domain.includes('..')) return false;

  return true;
}
